export type TemplateFieldType = 'text' | 'image' | 'date' | 'time' | 'number' | 'boolean';

export interface TemplateFieldSpec {
  key: string;
  label: string;
  type: TemplateFieldType;
  required?: boolean;
  options?: string[];
}

export interface TemplateDataSchema {
  fields?: TemplateFieldSpec[];
}

/**
 * Parse a raw dataSchema object from DB/API into a validated list of field specs.
 * Returns `null` if empty or unparseable.
 * IMPORTANT: `null` means "supports everything" — never "supports nothing".
 */
export function parseTemplateSchema(raw: unknown): TemplateFieldSpec[] | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const obj = raw as Record<string, unknown>;

  const rawFields = Array.isArray(obj.fields)
    ? obj.fields
    : Array.isArray(raw)
      ? raw
      : null;

  if (!rawFields || rawFields.length === 0) {
    return null;
  }

  const fields: TemplateFieldSpec[] = [];

  for (const item of rawFields) {
    if (!item || typeof item !== 'object') continue;
    const f = item as Record<string, unknown>;
    if (typeof f.key !== 'string' || !f.key.trim()) continue;
    if (typeof f.label !== 'string' || !f.label.trim()) continue;

    const validTypes: TemplateFieldType[] = ['text', 'image', 'date', 'time', 'number', 'boolean'];
    const rawType = typeof f.type === 'string' ? f.type.toLowerCase() : 'text';
    const type: TemplateFieldType = validTypes.includes(rawType as TemplateFieldType)
      ? (rawType as TemplateFieldType)
      : 'text';

    fields.push({
      key: f.key.trim(),
      label: f.label.trim(),
      type,
      ...(typeof f.required === 'boolean' ? { required: f.required } : {}),
      ...(Array.isArray(f.options) ? { options: f.options.map(String) } : {}),
    });
  }

  return fields.length > 0 ? fields : null;
}
